import { ChangeEvent, FC } from 'react';
import { NumberFormatValues, SourceInfo } from 'react-number-format';
import { Buttons, Form, FormElements } from '../../../common/AddEditFormLayout/AddEditFormLayout.styles';
import {
  DropdownWrapperLarge,
  DropdownWrapperMedium,
  InputWrapperLarge,
  InputWrapperMedium,
} from '../../../common/FormElementWrappers/FormElementWrappers.styles';
import Header from '../../../common/Header/Header';
import { H2 } from '../../../common/Headings/Headings.styles';
import Button from '../../../components/Button/Button';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Input from '../../../components/Input/Input';
import MaskedInput from '../../../components/MaskedInput/MaskedInput';
import RadioGroup from '../../../components/RadioGroup/RadioGroup';
import { RadioGroupLayouts } from '../../../components/RadioGroup/RadioGroup.types';
import { PriceOptionKeys, PRICE_OPTION, STATUS, StatusKeys } from '../../../constants/ApiEnums';
import { OPTIONS_PLACEHOLDER } from '../../../constants/Dropdown';
import { HorizontalLine, RadioWrapper, RowWrapper, SubTitle, SubTitleWrapper } from './ClassForm.styles';
import { ClassFormProps } from './ClassForm.types';
import ImageForm from '../../../components/ImageForm/ImageForm';
import { readOnlyLabelTableText } from '../../../constants/GlobalTexts';

const ClassForm: FC<ClassFormProps> = ({
  headerTitle,
  formValues,
  formErrors,
  setFormErrors,
  setFormValues,
  handleSave,
  saveDisabled,
  isButtonLoading,
  disabledInputs = [],
  hasImageForm = false,
  classNoInputRef,
}) => {
  const handleDropdown = (event: ChangeEvent<HTMLSelectElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
    setFormErrors && setFormErrors({ ...formErrors, [event.target.id]: '' });
  };

  const handleInput = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
    setFormErrors && setFormErrors({ ...formErrors, [event.target.id]: '' });
  };

  const handleValidOnWebChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, validOnWeb: event.target.id === 'validOnWebYes' });
    setFormErrors && setFormErrors({ ...formErrors, validOnWeb: '' });
  };

  const handleAllowHalfChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, allowHalf: event.target.id === 'allowHalfYes' });
    setFormErrors && setFormErrors({ ...formErrors, allowHalf: '' });
  };

  const handleAllowDiscountChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, allowDiscount: event.target.id === 'allowDiscountYes' });
    setFormErrors && setFormErrors({ ...formErrors, allowDiscount: '' });
  };

  const handlePrintInKitchenChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, printInKitchen: event.target.id === 'printInKitchenYes' });
    setFormErrors && setFormErrors({ ...formErrors, printInKitchen: '' });
  };

  const handleTaxExemptedChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, taxExempted: event.target.id === 'taxExemptedYes' });
    setFormErrors && setFormErrors({ ...formErrors, taxExempted: '' });
  };

  const handleMaskedInputChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    if (sourceInfo.event) {
      setFormValues({
        ...formValues,
        [sourceInfo.event.target.id]: values.value === '' ? '' : +values.value,
      });
      setFormErrors && setFormErrors({ ...formErrors, [sourceInfo.event.target.id]: '' });
    }
  };

  return (
    <>
      <Header breadcrumb={{ label: 'Back to Class Table', to: '/tables/classes' }} title={headerTitle} />
      <Form>
        <FormElements>
          <H2>Class Table</H2>
          <RowWrapper>
            <InputWrapperMedium>
              <Input
                label='MOA Class Number'
                id='moaClassNo'
                value={formValues?.classNo?.toString()}
                disabled={disabledInputs.includes('moaClassNo')}
              />
            </InputWrapperMedium>            <InputWrapperMedium>
              <MaskedInput
                label='Class Number'
                id='classNo'
                value={formValues?.classNo?.toString()}
                errorText={formErrors?.classNo}
                onValueChange={handleMaskedInputChange}
                isAllowed={(values) => +values.value < 2147483648}
                disabled={disabledInputs.includes('classNo')}
                {...(classNoInputRef && { ref: classNoInputRef })}
              />
            </InputWrapperMedium>
          </RowWrapper>
          <RowWrapper>
            <InputWrapperLarge>
              <Input
                label='Short Description'
                id='description'
                value={formValues?.description}
                onChange={handleInput}
                errorText={formErrors?.description}
                disabled={disabledInputs.includes('description')}
              />
            </InputWrapperLarge>
            <InputWrapperLarge>
              <Input
                label='Long Description'
                id='longDescription'
                value={formValues?.longDescription}
                onChange={handleInput}
                errorText={formErrors?.longDescription}
                disabled={disabledInputs.includes('longDescription')}
              />
            </InputWrapperLarge>
          </RowWrapper>
          <RowWrapper>
            <DropdownWrapperMedium>
              <Dropdown
                label='Status'
                id='statusNo'
                options={Object.keys(STATUS).map((key: any) => {
                  return { value: key, label: `${key} - ${STATUS[key as StatusKeys]}` };
                })}
                value={formValues?.statusNo?.toString() || OPTIONS_PLACEHOLDER}
                errorText={formErrors?.statusNo}
                onChange={handleDropdown}
                disabled={disabledInputs.includes('statusNo')}
              />
            </DropdownWrapperMedium>
            <InputWrapperMedium>
              <MaskedInput
                label='PCA Class Number'
                id='pcaClassNo'
                value={formValues?.pcaClassNo?.toString()}
                errorText={formErrors?.pcaClassNo}
                onValueChange={handleMaskedInputChange}
                isAllowed={(values) => +values.value < 2147483648}
                disabled={disabledInputs.includes('pcaClassNo')}
              />
            </InputWrapperMedium>
          </RowWrapper>
          <RowWrapper>
            <DropdownWrapperLarge>
              <Dropdown
                label='Price Option Number'
                id='priceOptionNo'
                options={Object.keys(PRICE_OPTION).map((key: any) => {
                  return { value: key, label: `${key} - ${PRICE_OPTION[key as PriceOptionKeys]}` };
                })}
                value={formValues?.priceOptionNo?.toString() || OPTIONS_PLACEHOLDER}
                errorText={formErrors?.priceOptionNo}
                onChange={handleDropdown}
                disabled={disabledInputs.includes('statusNo')}
              />
            </DropdownWrapperLarge>
          </RowWrapper>
          <RowWrapper>
            <RadioWrapper>
              <RadioGroup
                options={[
                  {
                    id: 'printInKitchenYes',
                    label: 'Yes',
                    disabled: disabledInputs.includes('printInKitchen'),
                  },
                  {
                    id: 'printInKitchenNo',
                    label: 'No',
                    disabled: disabledInputs.includes('printInKitchen'),
                  },
                ]}
                id='printInKitchen'
                label='Print In Kitchen?'
                layout={RadioGroupLayouts.Row}
                selected={
                  formValues?.printInKitchen === true
                    ? 'printInKitchenYes'
                    : formValues?.printInKitchen === false
                    ? 'printInKitchenNo'
                    : undefined
                }
                errorText={formErrors?.printInKitchen}
                onChange={handlePrintInKitchenChange}
              />
            </RadioWrapper>
            <RadioWrapper>
              <RadioGroup
                options={[
                  {
                    id: 'allowDiscountYes',
                    label: 'Yes',
                    disabled: disabledInputs.includes('allowDiscount'),
                  },
                  {
                    id: 'allowDiscountNo',
                    label: 'No',
                    disabled: disabledInputs.includes('allowDiscount'),
                  },
                ]}
                id='allowDiscount'
                label='Allow Discount?'
                layout={RadioGroupLayouts.Row}
                selected={
                  formValues?.allowDiscount === true
                    ? 'allowDiscountYes'
                    : formValues?.allowDiscount === false
                    ? 'allowDiscountNo'
                    : undefined
                }
                errorText={formErrors?.allowDiscount}
                onChange={handleAllowDiscountChange}
              />
            </RadioWrapper>
          </RowWrapper>
          <RowWrapper>
            <RadioWrapper>
              <RadioGroup
                options={[
                  {
                    id: 'taxExemptedYes',
                    label: 'Yes',
                    disabled: disabledInputs.includes('taxExempted'),
                  },
                  {
                    id: 'taxExemptedNo',
                    label: 'No',
                    disabled: disabledInputs.includes('taxExempted'),
                  },
                ]}
                id='taxExempted'
                label='Taxable?'
                layout={RadioGroupLayouts.Row}
                selected={
                  formValues?.taxExempted === true
                    ? 'taxExemptedYes'
                    : formValues?.taxExempted === false
                    ? 'taxExemptedNo'
                    : undefined
                }
                errorText={formErrors?.taxExempted}
                onChange={handleTaxExemptedChange}
              />
            </RadioWrapper>
            <RadioWrapper>
              <RadioGroup
                options={[
                  {
                    id: 'allowHalfYes',
                    label: 'Yes',
                    disabled: disabledInputs.includes('allowHalf'),
                  },
                  {
                    id: 'allowHalfNo',
                    label: 'No',
                    disabled: disabledInputs.includes('allowHalf'),
                  },
                ]}
                id='allowHalf'
                label='Allow Half?'
                layout={RadioGroupLayouts.Row}
                selected={
                  formValues?.allowHalf === true
                    ? 'allowHalfYes'
                    : formValues?.allowHalf === false
                    ? 'allowHalfNo'
                    : undefined
                }
                errorText={formErrors?.allowHalf}
                onChange={handleAllowHalfChange}
              />
            </RadioWrapper>
          </RowWrapper>
          <RowWrapper>
            <InputWrapperMedium>
              <Input label='Mnemonic' id='mnemonic' value={formValues?.mnemonic} disabled />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <MaskedInput
                label='Sequence Number'
                id='seqNo'
                value={formValues?.seqNo?.toString()}
                errorText={formErrors?.seqNo}
                onValueChange={handleMaskedInputChange}
                isAllowed={(values) => +values.value < 2147483648}
                disabled={disabledInputs.includes('seqNo')}
              />
            </InputWrapperMedium>
          </RowWrapper>
          <RowWrapper>
            <RadioWrapper>
              <RadioGroup
                options={[
                  {
                    id: 'validOnWebYes',
                    label: 'Yes',
                    disabled: disabledInputs.includes('validOnWeb'),
                  },
                  {
                    id: 'validOnWebNo',
                    label: 'No',
                    disabled: disabledInputs.includes('validOnWeb'),
                  },
                ]}
                id='validOnWeb'
                label='Valid on Web?'
                layout={RadioGroupLayouts.Row}
                selected={
                  formValues?.validOnWeb === true
                    ? 'validOnWebYes'
                    : formValues?.validOnWeb === false
                    ? 'validOnWebNo'
                    : undefined
                }
                errorText={formErrors?.validOnWeb}
                onChange={handleValidOnWebChange}
              />
            </RadioWrapper>
            <RowWrapper>
              <InputWrapperLarge>
                <Input
                  label='BOH Description'
                  id='displayDesc'
                  value={formValues?.displayDesc}
                  onChange={handleInput}
                  errorText={formErrors?.displayDesc}
                  disabled={disabledInputs.includes('displayDesc')}
                />
              </InputWrapperLarge>
            </RowWrapper>
          </RowWrapper>
          {formValues && hasImageForm && formValues.classNo && (
            <>
              <HorizontalLine />
              <ImageForm resourceNo={[formValues.classNo]} attribute={'clss'} />
            </>
          )}
        </FormElements>
        <Buttons>
          <Button label='Save' onClick={handleSave} disabled={saveDisabled} isLoading={isButtonLoading} />
        </Buttons>
      </Form>
      <SubTitleWrapper>
      {readOnlyLabelTableText ?? 
         <SubTitle>{readOnlyLabelTableText}</SubTitle>
      }
      </SubTitleWrapper>
    </>
  );
};

export default ClassForm;
